// pusherTargetStore.js
// Tab-local pusher state. Pusher runs on each browser tab's main thread with
// its own socket, so subscription targets / config / auth headers live in
// this module scope instead of being smuggled through a
// ServiceWorkerMessenger scope and merged across tabs over RPC.
'use strict';

let target = {};
let config = {};
let headers = {};
let record = {};

/**
 * All channel subscription targets of this tab. This is the desired-state
 * store consumed by `processPusherTargets`: the subscription reference map
 * lets it reconcile component-level subscribe requests with this tab's
 * actual socket channels, including delayed unsubscribe and re-auth state.
 * @returns {Object<string, object>} keyed by channel name.
 * @example
 * getPusherTargets();
 * // => { 'private-enc-stream@A': { channelName: 'private-enc-stream@A', ... } }
 */
const getPusherTargets = () => target;

/**
 * @param {object} params
 * @param {string} params.channelName
 * @param {object} params.subscriptionData
 * @returns {void}
 * @example
 * setPusherChannelTarget({
 *   channelName: 'private-enc-stream@A',
 *   subscriptionData: { channelName: 'private-enc-stream@A', isCritical: true },
 * });
 */
const setPusherChannelTarget = ({ channelName, subscriptionData }) => {
  // Keep the full target data because reconciliation needs more than the
  // channel name: it uses subscriptionMap, criticality, and expiryTimestamp
  // to decide whether and how the socket should subscribe.
  target[channelName] = subscriptionData;
};

/**
 * Latest pusher config, updated whenever remote config merges. Tab-local and
 * single-writer (see PusherEmitter.jsx), so unlike the Service Worker era
 * there is no cross-tab conflict to key by host — the tab has exactly one
 * socket (see serviceWorker/pusher.js), and any config change that would
 * matter to that socket already forces a re-init (see
 * PusherEmitter.componentDidUpdate and pusher.js connect()'s reuse check),
 * so the latest stored value is always the one the live socket matches.
 * @returns {object|undefined} the stored config.
 * @example
 * getPusherConfig();
 * // => { appId: '550591', authEndpoint: 'https://...' }
 */
const getPusherConfig = () => config;

/**
 * @param {object} params
 * @param {object} params.pusherConfig
 * @returns {void}
 * @example
 * setPusherConfig({ pusherConfig });
 */
const setPusherConfig = ({ pusherConfig }) => {
  config = pusherConfig;
};

/**
 * Latest pusher auth headers (updated whenever the access token changes).
 * @returns {object} the stored headers.
 * @example
 * getPusherAuthHeaders();
 * // => { authorization: 'Bearer ...' }
 */
const getPusherAuthHeaders = () => headers;

/**
 * @param {object} params
 * @param {object} params.headers
 * @returns {void}
 * @example
 * setPusherAuthHeaders({ headers: { authorization: 'Bearer ...' } });
 */
const setPusherAuthHeaders = ({ headers: newHeaders }) => {
  headers = newHeaders;
};

/**
 * Per-channel subscribe bookkeeping (retriedCount / isSubscribing /
 * fetchedTimestamp) used for the batch-authenticate backoff.
 * @returns {Object<string, object>} keyed by channel name.
 * @example
 * getPusherSubscribeRecord();
 * // => { 'private-enc-stream@A': { retriedCount: 1, isSubscribing: false } }
 */
const getPusherSubscribeRecord = () => record;

/**
 * Reset every slice of the store. Only meant for tests.
 * @returns {void}
 * @example
 * resetPusherTargetStore();
 */
const resetPusherTargetStore = () => {
  target = {};
  config = {};
  headers = {};
  record = {};
};

export {
  getPusherTargets,
  setPusherChannelTarget,
  getPusherConfig,
  setPusherConfig,
  getPusherAuthHeaders,
  setPusherAuthHeaders,
  getPusherSubscribeRecord,
  resetPusherTargetStore,
};
